import './login.css';

import { IonList, IonText } from '@ionic/react';
import { Form, Formik } from 'formik';
import { ReactElement, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router';
import * as yup from 'yup';
import {
  AuthChallenge,
  Workspace,
  useAuthenticateWithPasswordMutation,
  useConfirmForgotPasswordMutation,
  useForgotPasswordMutation,
  useRespondToAuthChallengeMutation,
} from '../../api/graphql/generated';
import { useBeaconRouter } from '../../route/route.hooks';
import { useNavToLastKnownLocation } from '../../store/nav-history/nav-history.hooks';
import { useWorkspaceHelpers } from '../../store/workspace/workspace.hooks';
import { logger } from '../../util/logger';
import { useQueryParameters } from '../../util/router';
import { BaseButton } from '../button/base-button';
import { FormSubmitButton } from '../form/form-submit.button';
import { IonFormField } from '../form/ion-form.field';
import { LoginWrapper } from './login-wrapper';

interface FormValues {
  forgotPassword?: boolean;
  challenge?: AuthChallenge;
  email: string | null;
  username: string | null;
  confirmationCode?: string;
  password?: string;
  confirmPassword?: string;
}

const LoginPasswordConfirm = (): ReactElement => {
  const { i18n } = useTranslation();

  const schema = yup.object().shape({
    forgotPassword: yup.boolean(),
    email: yup
      .string()
      .email(i18n.t('common:login_invalid_email'))
      .required('*'),
    username: yup.string().required(),
    password: yup
      .string()
      .required('*')
      .matches(
        /^(?=.*[a-z])(?=.*[0-9])(?=.{8,})/,
        i18n.t('common:password_complexity')
      ),
    confirmPassword: yup
      .string()
      .oneOf(
        [yup.ref('password'), undefined],
        i18n.t('common:passwords_must_match')
      ),
    confirmationCode: yup.string().when('forgotPassword', {
      is: true,
      then: (schema) => schema.required('*'),
    }),
  });

  const queryParams = useQueryParameters();
  const email = queryParams.get('email');
  const username = queryParams.get('username');

  const { upsertWorkspace } = useWorkspaceHelpers();
  const [isErrorState, setErrorState] = useState<boolean>(false);
  const { navToLastKnownTenantLocation } = useNavToLastKnownLocation();

  const { push } = useHistory();
  const [authWithPassword, { loading, error }] =
    useAuthenticateWithPasswordMutation({ fetchPolicy: 'network-only' });

  const [
    respondToAuthChallengeLazy,
    { loading: respondToAuthChallengeLoading, error: respondToAuthError },
  ] = useRespondToAuthChallengeMutation({ fetchPolicy: 'network-only' });

  const [forgotPasswordLazy] = useForgotPasswordMutation({
    fetchPolicy: 'network-only',
  });

  const [
    confirmForgotPasswordLazy,
    {
      loading: confirmForgotPasswordLoading,
      error: confirmForgotPasswordError,
    },
  ] = useConfirmForgotPasswordMutation({ fetchPolicy: 'network-only' });

  const { builder } = useBeaconRouter();

  const [challenge, setChallenge] = useState<AuthChallenge>();
  const [forgotPassword, setForgotPassword] = useState<boolean>();

  const formValues = useMemo((): FormValues => {
    return {
      email,
      username,
      challenge,
      forgotPassword,
    };
  }, [username, email, challenge, forgotPassword]);

  const onForgotPassword = async () => {
    forgotPasswordLazy({
      variables: {
        input: {
          username,
        },
      },
    });
    setForgotPassword(true);
  };

  const onSubmit = async (form: FormValues) => {
    const { confirmationCode, challenge, username, password, forgotPassword } =
      form;

    let workspace: Workspace | undefined;

    if (forgotPassword) {
      if (confirmationCode && username && password) {
        const response = await confirmForgotPasswordLazy({
          variables: {
            input: {
              confirmationCode,
              password: password,
              username,
            },
          },
        });

        workspace = response.data?.confirmForgotPassword.workspace as Workspace;
      }
    } else if (challenge) {
      if (password) {
        const response = await respondToAuthChallengeLazy({
          variables: {
            input: {
              challengeName: challenge.challengeName,
              session: challenge.session,
              username: challenge.username,
              answer: password,
            },
          },
        });

        const workspaces = response.data?.respondToAuthChallenge.workspaces;

        if (workspaces) {
          // the respond to auth challenge api returns multiple workspaces. eventually it will only return one
          // so lets take the first and use it
          workspace = workspaces[0] as Workspace;
        }
      }
    } else {
      if (username && password) {
        logger.info('authenticating user with password', { username });
        const authResponse = await authWithPassword({
          variables: { input: { username, password } },
        });

        const challenge = authResponse.data?.authenticateWithPassword.challenge;

        if (challenge) {
          if (challenge.challengeName === 'NEW_PASSWORD_REQUIRED') {
            setChallenge(challenge);
          }
        } else if (authResponse.data?.authenticateWithPassword.workspace) {
          workspace = authResponse.data?.authenticateWithPassword.workspace as
            | Workspace
            | undefined;
        }
      }
    }

    if (workspace) {
      logger.info('upserting workspace after password auth');
      await upsertWorkspace(workspace);
      navToLastKnownTenantLocation(workspace.tenant.id);
    }
  };

  useEffect(() => {
    if (error || respondToAuthError || confirmForgotPasswordError) {
      logger.info('error authenticating user with password');
      setErrorState(true);
    }
  }, [error, respondToAuthError, confirmForgotPasswordError]);

  const isLoading =
    respondToAuthChallengeLoading === true ||
    confirmForgotPasswordLoading === true ||
    loading === true;

  return (
    <LoginWrapper>
      <Formik
        validationSchema={schema}
        initialValues={formValues}
        enableReinitialize={true}
        onSubmit={onSubmit}
        validateOnMount={true}
      >
        {({ isValid, resetForm, values }) => {
          const confirmPassword =
            forgotPassword === true || challenge !== undefined;

          return (
            <Form>
              <IonList lines="none">
                <IonFormField
                  label={i18n.t('common:email')}
                  name="email"
                  inputProps={{
                    readonly: true,
                    disabled: true,
                  }}
                />
                {forgotPassword && (
                  <IonFormField
                    label={i18n.t('common:confirmation_code')}
                    name="confirmationCode"
                  />
                )}
                <IonFormField
                  label={
                    values.challenge
                      ? i18n.t('common:new_password')
                      : i18n.t('common:password')
                  }
                  name="password"
                  inputProps={{
                    type: 'password',
                    autofocus: true,
                    onFocus: () => {
                      if (isErrorState) {
                        setErrorState(false);
                        resetForm();
                      }
                    },
                  }}
                />
                {confirmPassword && (
                  <IonFormField
                    label={i18n.t('common:confirm_password')}
                    name="confirmPassword"
                    inputProps={{
                      type: 'password',
                    }}
                  />
                )}
              </IonList>
              <br />
              <FormSubmitButton
                loading={isLoading}
                disabled={!isValid}
                label={i18n.t('common:login')}
                placement="center"
              />
            </Form>
          );
        }}
      </Formik>
      <div className="secondary-buttons-grid">
        <BaseButton
          className="left"
          buttonProps={{
            fill: 'clear',
            onClick: onForgotPassword,
          }}
        >
          {i18n.t('common:forgot_password')}
        </BaseButton>
        <BaseButton
          className="right"
          buttonProps={{
            fill: 'clear',
            onClick: () => {
              push(builder.getAuthLoginRoute());
            },
          }}
        >
          {i18n.t('common:login_start_over')}
        </BaseButton>
      </div>
      {isErrorState && (
        <div className="content-center">
          <IonText color="danger">
            {i18n.t('common:login_username_password_error')}
          </IonText>
        </div>
      )}
    </LoginWrapper>
  );
};

export { LoginPasswordConfirm };
